import React, { useState } from 'react';
import { Item } from '@/stores/canvasStore';
import { cn } from '@/lib/utils';

interface TweetContent {
  tweet_id: string;
  tweet_url: string;
  author: {
    name: string;
    username: string;
    profile_image_url: string;
  };
  text: string;
  created_at: string;
  metrics: {
    likes: number;
    retweets: number;
    replies: number;
    bookmarks: number;
    views: number;
  };
  media?: Array<{
    type: string;
    url: string;
    preview_url?: string;
    width?: number;
    height?: number;
  }>;
  entities?: {
    urls?: Array<{ expanded_url: string; display_url: string }>;
    hashtags?: Array<{ tag: string }>;
    mentions?: Array<{ username: string }>;
  };
}

interface TweetPreviewCardProps {
  item: Item;
}

/** Format large numbers compactly (1200 → 1.2K, 1500000 → 1.5M) */
function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

/** Format ISO date to readable string */
function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

/** Check if an item has tweet content (use to decide which card to render) */
export function isTweetItem(item: Item): boolean {
  const content = item.content as Record<string, unknown>;
  return item.item_type === 'link' && !!content?.tweet_id;
}

export function TweetPreviewCard({ item }: TweetPreviewCardProps) {
  const content = item.content as unknown as TweetContent;
  const [avatarError, setAvatarError] = useState(false);
  const [mediaError, setMediaError] = useState(false);

  const handleOpenTweet = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    window.open(content.tweet_url, '_blank', 'noopener,noreferrer');
  };

  const firstMedia =
    content.media && content.media.length > 0 ? content.media[0] : null;
  const hasMedia = firstMedia && !mediaError;

  return (
    <div
      className={cn(
        'w-full h-full rounded-2xl overflow-hidden flex flex-col cursor-pointer',
        'bg-card border border-border hover:border-primary/40 transition-colors',
        'dark:bg-[#16181C] dark:border-[#2F3336]'
      )}
      onClick={handleOpenTweet}
      style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
    >
      {/* Author row */}
      <div className="flex items-center gap-2.5 px-4 pt-3 pb-1">
        {content.author.profile_image_url && !avatarError ? (
          <img
            src={content.author.profile_image_url}
            alt={content.author.name}
            className="w-10 h-10 rounded-full flex-shrink-0"
            onError={() => setAvatarError(true)}
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-muted-foreground">
              {content.author.name?.[0]?.toUpperCase() || '?'}
            </span>
          </div>
        )}
        <div className="min-w-0">
          <div className="font-bold text-[15px] text-foreground truncate leading-tight">
            {content.author.name}
          </div>
          <div className="text-[15px] text-[#536471] dark:text-[#71767B] truncate leading-tight">
            @{content.author.username}
          </div>
        </div>
      </div>

      {/* Tweet text */}
      <div className="px-4 py-2 flex-1 min-h-0 overflow-hidden">
        <p className="text-[15px] leading-5 text-foreground whitespace-pre-wrap break-words line-clamp-6">
          {content.text}
        </p>
      </div>

      {/* Media preview */}
      {hasMedia && (
        <div className="px-4 pb-2">
          <div className="relative rounded-2xl overflow-hidden border border-border/50 dark:border-[#2F3336]">
            <img
              src={firstMedia.url || firstMedia.preview_url || ''}
              alt=""
              className="w-full object-cover"
              style={{ maxHeight: 280 }}
              onError={() => setMediaError(true)}
            />
            {firstMedia.type === 'video' && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <div className="w-12 h-12 rounded-full bg-[#1D9BF0] flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white ml-0.5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Engagement metrics */}
      <div className="flex items-center gap-4 px-4 py-1.5 text-[13px] text-[#536471] dark:text-[#71767B]">
        {/* Replies */}
        <span className="flex items-center gap-1">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M1.751 10c0-4.42 3.584-8 8.005-8h4.366c4.49 0 8.129 3.64 8.129 8.13 0 2.25-.893 4.306-2.394 5.82l-5.643 5.69a.75.75 0 0 1-1.065 0l-5.643-5.69A8.19 8.19 0 0 1 1.751 10zm8.005-6.5A6.505 6.505 0 0 0 3.251 10c0 1.77.693 3.423 1.929 4.69l5.07 5.11 5.07-5.11A6.69 6.69 0 0 0 17.371 10c0-3.39-2.742-6.13-6.129-6.13H9.756v-.37z" />
          </svg>
          {formatCount(content.metrics.replies)}
        </span>
        {/* Retweets */}
        <span className="flex items-center gap-1">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M4.5 3.88l4.432 4.14-1.364 1.46L5.5 7.55V16c0 1.1.896 2 2 2H13v2H7.5c-2.209 0-4-1.79-4-4V7.55L1.432 9.48.068 8.02 4.5 3.88zM16.5 6H11V4h5.5c2.209 0 4 1.79 4 4v8.45l2.068-1.93 1.364 1.46-4.432 4.14-4.432-4.14 1.364-1.46 2.068 1.93V8c0-1.1-.896-2-2-2z" />
          </svg>
          {formatCount(content.metrics.retweets)}
        </span>
        {/* Likes */}
        <span className="flex items-center gap-1">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16.697 5.5c-1.222-.06-2.679.51-3.89 2.16l-.805 1.09-.806-1.09C9.984 6.01 8.526 5.44 7.304 5.5c-1.243.07-2.349.78-2.91 1.91-.552 1.12-.633 2.78.479 4.82 1.074 1.97 3.257 4.27 7.129 6.61 3.87-2.34 6.052-4.64 7.126-6.61 1.111-2.04 1.03-3.7.477-4.82-.561-1.13-1.666-1.84-2.908-1.91zm4.187 7.69c-1.351 2.48-4.001 5.12-8.379 7.67l-.503.3-.504-.3c-4.379-2.55-7.029-5.19-8.382-7.67-1.36-2.5-1.41-4.86-.514-6.67.887-1.79 2.647-2.91 4.601-3.01 1.651-.09 3.368.56 4.798 2.01 1.429-1.45 3.146-2.1 4.796-2.01 1.954.1 3.714 1.22 4.601 3.01.896 1.81.846 4.17-.514 6.67z" />
          </svg>
          {formatCount(content.metrics.likes)}
        </span>
        {/* Views */}
        {content.metrics.views > 0 && (
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.75 21V3h2v18h-2zM18.75 21V8.5h2V21h-2zM13.75 21v-9h2v9h-2zM3.75 21v-4h2v4h-2z" />
            </svg>
            {formatCount(content.metrics.views)}
          </span>
        )}
      </div>

      {/* Date */}
      <div className="px-4 py-1 text-[13px] text-[#536471] dark:text-[#71767B]">
        {formatDate(content.created_at)}
      </div>

      {/* Footer: X branding */}
      <div className="flex items-center gap-2 px-4 py-2 border-t border-border/50 dark:border-[#2F3336] mt-auto">
        <svg className="w-4 h-4 text-foreground" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
        <span className="text-xs text-[#536471] dark:text-[#71767B]">
          Posted on X
        </span>
      </div>
    </div>
  );
}
